import { notifications } from '@mantine/notifications'
import type { AppRouter } from '@markku/backend/trpc/router'
import type { QueryOptions } from '@markku/types'
import type { CreateTRPCReact } from '@trpc/react-query'

/**
 * Creates chat-related hooks with the provided tRPC client.
 * This factory function encapsulates all chat logic (queries, mutations, notifications)
 * making the chat components truly self-contained and reusable across apps.
 *
 * Full type safety is preserved - all return types are inferred from the AppRouter.
 *
 * @param trpc - The typed tRPC client from the consuming app
 * @returns Object containing all chat-related hooks with full type inference
 */
export function createChatHooks(trpc: CreateTRPCReact<AppRouter, unknown>) {
  return {
    // ========================================================================
    // Queries
    // ========================================================================

    /**
     * Get all chat rooms for the current user with metadata (unread counts, last message)
     */
    useGetRooms: (options?: QueryOptions) => {
      return trpc.chatCustom.getRooms.useQuery(undefined, options)
    },

    /**
     * Get a single chat room by ID (includes participants)
     */
    useGetRoom: (roomId: number, options?: QueryOptions) => {
      return trpc.chatRoom.getById.useQuery({ id: roomId }, options)
    },

    /**
     * List messages in a room with optional filters
     */
    useListMessages: ({
      roomId,
      lastMessageCreatedAt,
      limit = 100,
      options,
    }: {
      roomId: number
      lastMessageCreatedAt?: string
      limit?: number
      options?: QueryOptions
    }) => {
      return trpc.chatMessage.list.useQuery(
        {
          limit,
          filters: {
            roomId: { eq: roomId },
            ...(lastMessageCreatedAt
              ? { createdAt: { gt: lastMessageCreatedAt } }
              : {}),
          },
          orderBy: 'createdAt',
          orderDirection: 'asc',
        },
        options,
      )
    },

    // ========================================================================
    // Mutations - Rooms
    // ========================================================================

    /**
     * Create a new chat room
     */
    useCreateRoom: () => {
      const utils = trpc.useUtils()
      return trpc.chatRoom.create.useMutation({
        onSuccess: () => {
          notifications.show({
            title: 'Huone luotu',
            message: 'Keskusteluhuone on luotu',
            color: 'green',
          })
          utils.chatCustom.getRooms.invalidate()
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          notifications.show({
            title: 'Virhe',
            message: error?.message || 'Huoneen luonti epäonnistui',
            color: 'red',
          })
        },
      })
    },

    // ========================================================================
    // Mutations - Messages
    // ========================================================================

    /**
     * Send a message to a room
     * Invalidates cache and waits for refetch to prevent race conditions
     */
    useSendMessage: () => {
      const utils = trpc.useUtils()
      return trpc.chatMessage.create.useMutation({
        onSuccess: async () => {
          // Invalidate and wait for refetch to complete
          // This prevents race conditions and keeps messages in order
          await utils.chatMessage.list.invalidate()
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          notifications.show({
            title: 'Virhe',
            message: error?.message || 'Viestin lähetys epäonnistui',
            color: 'red',
          })
        },
      })
    },

    /**
     * Delete a message
     */
    useDeleteMessage: () => {
      const utils = trpc.useUtils()
      return trpc.chatMessage.delete.useMutation({
        onSuccess: () => {
          utils.chatMessage.list.invalidate()
          notifications.show({
            message: 'Viesti poistettu',
            color: 'green',
          })
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          notifications.show({
            title: 'Virhe',
            message: error.message || 'Viestin poisto epäonnistui',
            color: 'red',
          })
        },
      })
    },

    // ========================================================================
    // Mutations - Participants
    // ========================================================================

    /**
     * Add participants to a room (admin only)
     */
    useAddParticipants: () => {
      const utils = trpc.useUtils()
      return trpc.chatCustom.addParticipants.useMutation({
        onSuccess: () => {
          utils.chatRoom.getById.invalidate()
          notifications.show({
            message: 'Osallistujat lisätty',
            color: 'green',
          })
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          notifications.show({
            title: 'Virhe',
            message: error?.message || 'Osallistujien lisääminen epäonnistui',
            color: 'red',
          })
        },
      })
    },

    /**
     * Remove a participant from a room (admin only)
     */
    useRemoveParticipant: () => {
      const utils = trpc.useUtils()
      return trpc.chatCustom.removeParticipant.useMutation({
        onSuccess: () => {
          utils.chatRoom.getById.invalidate()
          notifications.show({
            message: 'Osallistuja poistettu',
            color: 'green',
          })
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          notifications.show({
            title: 'Virhe',
            message: error?.message || 'Osallistujan poisto epäonnistui',
            color: 'red',
          })
        },
      })
    },

    // ========================================================================
    // Mutations - Read Markers
    // ========================================================================

    /**
     * Mark messages as read (silent mutation, no notification)
     */
    useMarkAsRead: () => {
      return trpc.chatCustom.markAsRead.useMutation()
    },

    // ========================================================================
    // Attachments
    // ========================================================================

    /**
     * Create an attachment for chat messages
     */
    useCreateAttachment: () => {
      return trpc.attachment.createAttachment.useMutation()
    },

    // ========================================================================
    // Customer Service Room
    // ========================================================================

    /**
     * Find or create customer service room for a customer
     * Creates room on first access and adds all customer members
     */
    useFindOrCreateCustomerServiceRoom: () => {
      const utils = trpc.useUtils()
      return trpc.chatCustom.findOrCreateCustomerServiceRoom.useMutation({
        onSuccess: () => {
          utils.chatCustom.getRooms.invalidate()
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          notifications.show({
            title: 'Virhe',
            message:
              error?.message || 'Asiakaspalvelu-huoneen avaus epäonnistui',
            color: 'red',
          })
        },
      })
    },
  }
}

// Export the return type for use in component props
export type ChatHooks = ReturnType<typeof createChatHooks>
